"use client";

import { formatCurrency } from "@/utils/currencyFormatter";
import clsx from "clsx";
import { memo, useMemo } from "react";

// A generic day button used as the default renderer for calendar day cells.
// Supports optional price, currency formatting, and holiday indicator.
const DefaultDayButton = memo(
  ({
    day,
    children,
    getPriceByDate,
    currency,
    isHoliday = false,
    ...buttonProps
  }) => {
    const price = useMemo(
      () =>
        typeof getPriceByDate === "function" ? getPriceByDate(day.date) : null,
      [getPriceByDate, day.date],
    );

    const formattedPrice = useMemo(
      () => (price ? formatCurrency(price, currency) : null),
      [price, currency],
    );

    const isSelected = buttonProps?.modifiers?.selected;

    return (
      <button
        {...buttonProps}
        className="relative !mx-auto flex h-full !w-full cursor-pointer flex-col items-center justify-center"
      >
        {isHoliday && (
          <div className="bg-tertiary-emphasis absolute top-[2px] right-[2px] h-[6px] w-[6px] rounded-full"></div>
        )}
        <div
          className={clsx(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-semibold",
            isSelected && !buttonProps?.modifiers?.range_middle
              ? "bg-primary rounded-full text-white"
              : "",
            buttonProps?.modifiers?.range_middle && isSelected
              ? "text-primary rounded-none"
              : "",
          )}
        >
          <span>{children}</span>
        </div>
        <span className="text-foreground-muted h-full w-full flex-1 bg-white pt-[3px] text-xs leading-none font-thin">
          {formattedPrice}
        </span>
      </button>
    );
  },
);

DefaultDayButton.displayName = "DefaultDayButton";

export default DefaultDayButton;
